import { useState } from 'react'
import type { WorkRow } from '@workflow/contracts'
import { Icons, ProgressRing } from '@workflow/ui'
import { hoursFigure } from '@/features/jobs/jobsStatus'
import { Card, WeekBars, changeText, monthWeeks, previousMonth, type MonthWeek } from '@/features/overview/OverviewParts'
import { MonthSwitcher } from '@/features/jobs/MonthSwitcher'
import { monthKey, monthName } from '@/features/jobs/jobPeriods'
import { Section } from '@/shell/PageFrame'
import { isLive } from './workParts'

export interface MonthOverview {
  /** Hours on the work finished this month. */
  hours: number
  /** The same, the month before — what the change is measured from. */
  previousHours: number
  completed: number
  /** The studio's 1–5 on this month's finished work, when it rated any. */
  rating: number | null
  ratings: number
  weeks: MonthWeek[]
}

const inMonth = (iso: string | null, key: string): boolean => {
  if (!iso) return false
  const date = new Date(iso)
  return !Number.isNaN(date.getTime()) && monthKey(date) === key
}

/**
 * One month of a player's work, from the rows they already hold: finished
 * work counts in the month it was finished, and its hours with it; the
 * weeks are the month's days in sevens, so the bars always add up to the
 * headline.
 */
export function monthOverview(rows: readonly WorkRow[], key: string): MonthOverview {
  const finished = (month: string) => rows.filter((row) => row.completion === 'completed' && inMonth(row.completedAt, month))
  const done = finished(key)
  const sum = (list: WorkRow[]) => list.reduce((total, row) => total + (row.actualHours ?? 0), 0)
  const rated = done.filter((row) => row.quality !== null)

  const weeks = monthWeeks(key, done.map((row) => ({ day: new Date(row.completedAt!).getDate(), hours: row.actualHours ?? 0 })))

  return {
    hours: sum(done),
    previousHours: sum(finished(previousMonth(key))),
    completed: done.length,
    rating: rated.length ? rated.reduce((total, row) => total + row.quality!, 0) / rated.length : null,
    ratings: rated.length,
    weeks,
  }
}

/**
 * The player's month at a glance, on Home: what they finished, the hours it
 * held, and how the studio rated it — a few large figures on soft rounded
 * cards, read in a second, and any month a turn of the wheel away. No
 * grid of numbers: one figure per card, and the weeks as a quiet row of
 * bars with their hours written on them, so a phone needs no hover.
 */
export function PlayerOverview({ rows }: { rows: readonly WorkRow[] }) {
  const [month, setMonth] = useState(() => monthKey(new Date()))
  const view = monthOverview(rows, month)
  // What is still open is a fact about now, so only the current month says it.
  const current = month === monthKey(new Date())
  const open = current ? rows.filter(isLive).length : 0
  const before = monthName(previousMonth(month)).split(' ')[0]

  return (
    <Section title="Overview" aside={<MonthSwitcher value={month} onChange={setMonth} />}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Card className="col-span-2 sm:row-span-2">
          <p className="text-caption text-muted-foreground">Hours Worked</p>
          <p className="mt-1 flex items-baseline gap-1.5">
            <span className="font-mono text-5xl leading-none tabular-nums text-foreground">{hoursFigure(view.hours)}</span>
            <span className="text-ui text-muted-foreground">Hours</span>
          </p>
          <p className="mt-2 text-caption text-muted-foreground">{changeText(view.hours, view.previousHours, before!)}</p>
          <WeekBars weeks={view.weeks} />
        </Card>

        <Card>
          <div className="flex items-start justify-between gap-2">
            <p className="text-caption text-muted-foreground">Completed</p>
            {current && view.completed + open > 0 ? (
              <ProgressRing value={view.completed / (view.completed + open)} size={28} label="Finished against what is still open" />
            ) : null}
          </div>
          <p className="mt-3 font-mono text-3xl leading-none tabular-nums text-foreground">{view.completed}</p>
          <p className="mt-2 text-caption text-muted-foreground">
            {current && open > 0 ? `${view.completed === 1 ? 'Task' : 'Tasks'} Finished · ${open} Still Open` : `${view.completed === 1 ? 'Task' : 'Tasks'} Finished`}
          </p>
        </Card>

        <Card>
          <div className="flex items-start justify-between gap-2">
            <p className="text-caption text-muted-foreground">Rating</p>
            <span className="text-muted-foreground" aria-hidden><Icons.levelStar size="sm" /></span>
          </div>
          <p className="mt-3 flex items-baseline gap-1 font-mono text-3xl leading-none tabular-nums text-foreground">
            {view.rating === null ? '—' : view.rating.toFixed(1)}
            {view.rating !== null ? <span className="text-caption text-muted-foreground">/ 5</span> : null}
          </p>
          <p className="mt-2 text-caption text-muted-foreground">
            {view.ratings === 0 ? 'No Ratings Yet' : `From ${view.ratings} Rated ${view.ratings === 1 ? 'Task' : 'Tasks'}`}
          </p>
        </Card>
      </div>
    </Section>
  )
}

